import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A pointer that starts here belongs to that region. The card does not flip
 * and does not skip. Choice rows are `div[role="button"]`, not `<button>`.
 */
export const REGIAO_INTERATIVA =
  'button, a, [role="button"], [data-mask], [data-nogesture]';

interface UseCardGesturesOptions {
  /** Fired when the card is flung past the threshold. 1 = next, -1 = previous. */
  onSwipe: (dir: 1 | -1) => void;
  /** Fired on a tap (press with no meaningful horizontal movement). */
  onFlip: () => void;
  /** Horizontal distance (px) required to count as a swipe. */
  threshold?: number;
  /** When true, all gestures are ignored (e.g. during the fling-out animation). */
  disabled?: boolean;
}

/**
 * Pointer + touch gestures for the review card.
 *
 * - drag horizontally  -> live `dragX` (parent tilts/translates the card)
 * - release past threshold -> `onSwipe(dir)`
 * - press with no movement -> `onFlip()`
 * - a gesture that starts on `REGIAO_INTERATIVA` (a control, a link, a choice
 *   row, or an occlusion mask) belongs to that region: the card does not flip
 *   and does not skip.
 */
export function useCardGestures({
  onSwipe,
  onFlip,
  threshold = 60,
  disabled = false,
}: UseCardGesturesOptions) {
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const s = useRef({ active: false, moved: false, sx: 0, sy: 0 });

  const start = useCallback(
    (x: number, y: number, target: EventTarget | null) => {
      if (disabled) return;
      const el = target as HTMLElement | null;
      if (el && el.closest(REGIAO_INTERATIVA)) return;
      s.current = { active: true, moved: false, sx: x, sy: y };
      setDragging(true);
    },
    [disabled],
  );

  const move = useCallback((x: number, y: number) => {
    const st = s.current;
    if (!st.active) return;
    const dx = x - st.sx;
    const dy = y - st.sy;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 6) {
      st.moved = true;
      setDragX(dx);
    }
  }, []);

  const end = useCallback(
    (x: number) => {
      const st = s.current;
      if (!st.active) return;
      const dx = x - st.sx;
      st.active = false;
      setDragging(false);
      setDragX(0);
      if (Math.abs(dx) > threshold) onSwipe(dx < 0 ? 1 : -1);
      else if (!st.moved) onFlip();
    },
    [onSwipe, onFlip, threshold],
  );

  // Track the mouse beyond the element bounds while dragging.
  useEffect(() => {
    const mm = (e: MouseEvent) => move(e.clientX, e.clientY);
    const mu = (e: MouseEvent) => end(e.clientX);
    window.addEventListener('mousemove', mm);
    window.addEventListener('mouseup', mu);
    return () => {
      window.removeEventListener('mousemove', mm);
      window.removeEventListener('mouseup', mu);
    };
  }, [move, end]);

  const handlers = {
    onMouseDown: (e: React.MouseEvent) => start(e.clientX, e.clientY, e.target),
    onTouchStart: (e: React.TouchEvent) =>
      start(e.touches[0].clientX, e.touches[0].clientY, e.target),
    onTouchMove: (e: React.TouchEvent) =>
      move(e.touches[0].clientX, e.touches[0].clientY),
    onTouchEnd: (e: React.TouchEvent) => end(e.changedTouches[0].clientX),
  };

  return { dragX, dragging, handlers };
}
